/**
 * Arch Unit Composer
 *
 * Composes the complete arch hangar unit: floor + arch shell (roof) + end walls.
 * The arch shell group is named "roof" for the global show/hide toggle.
 */

import * as THREE from 'three'
import type { GeneratedUnit, DoorSpec } from '@/lib/templates/types'
import type { ArchHangarParams } from '@/lib/templates/arch-hangar'
import { renderFloor } from '../primitives/FloorRenderer'
import { renderArchShell, renderArchEndWallWithLength } from '../primitives/ArchRenderer'

export interface ArchUnitRenderOptions {
  wallColor?: string
  floorColor?: string
  enableShadows?: boolean
  /** Arch-specific params from type_config */
  archParams: ArchHangarParams
}

/**
 * Render a complete arch hangar unit: floor + arch shell + end walls
 */
export function renderArchUnit(
  unit: GeneratedUnit,
  options: ArchUnitRenderOptions
): THREE.Group {
  const {
    wallColor,
    floorColor,
    enableShadows = true,
    archParams,
  } = options

  const group = new THREE.Group()
  group.name = `unit-${unit.code}`
  group.userData = {
    unitCode: unit.code,
    unitType: unit.type,
    capacityStrategy: unit.capacity_strategy,
  }

  // 1. Floor (always visible)
  const floor = renderFloor(
    { width: archParams.span_ft, depth: archParams.length_ft },
    { color: floorColor, enableShadows }
  )
  group.add(floor)

  // 2. Arch shell — named "roof" for the global visibility toggle
  const roofGroup = new THREE.Group()
  roofGroup.name = 'roof'

  const archShell = renderArchShell({
    span_ft: archParams.span_ft,
    length_ft: archParams.length_ft,
    crown_height_ft: archParams.crown_height_ft,
    knee_wall_height_ft: archParams.knee_wall_height_ft,
    arch_profile: archParams.arch_profile,
    arch_segments: archParams.arch_segments,
    wall_thickness_ft: archParams.wall_thickness_ft,
    color: wallColor,
    enableShadows,
  })
  roofGroup.add(archShell)
  group.add(roofGroup)

  // 3. End walls (front and back) — always visible (separate from roof toggle)
  const endWallOptions = {
    span_ft: archParams.span_ft,
    crown_height_ft: archParams.crown_height_ft,
    knee_wall_height_ft: archParams.knee_wall_height_ft,
    arch_profile: archParams.arch_profile,
    arch_segments: archParams.arch_segments,
    wall_thickness_ft: archParams.wall_thickness_ft,
    color: wallColor,
    enableShadows,
  }

  // Front end wall
  const frontDoors: DoorSpec[] = unit.edges
    ?.find((e) => e.side === 'front')
    ?.openings || []
  const frontWall = renderArchEndWallWithLength('front', archParams.length_ft, {
    ...endWallOptions,
    doors: frontDoors,
  })
  group.add(frontWall)

  // Back end wall
  const backDoors: DoorSpec[] = unit.edges
    ?.find((e) => e.side === 'back')
    ?.openings || []
  const backWall = renderArchEndWallWithLength('back', archParams.length_ft, {
    ...endWallOptions,
    doors: backDoors,
  })
  group.add(backWall)

  return group
}
